import '../color-picker/qds-color-picker.js';
import type {
  QdsConfigField,
  QdsPropertyFieldPluginElement,
  QdsTypographyFontFamily,
  QdsTypographyStyle
} from '../../types/blocks.js';
import { loadBaseCSS } from '../shared/button-utils.js';
import {
  applyTypographyStyle,
  getTypographySummary,
  normalizeTypographyStyle
} from '../shared/typography-utils.js';

/**
 * @element qds-font-picker
 * @property {string} property - Field name emitted with selected typography
 * @property {object} value - Current typography value
 * @property {object} metadata - Property-editor metadata
 * @property {object} context - Optional host context
 * @property {object} target - Optional object currently being edited
 * @property {boolean} readonly - Disables editing controls
 * @fires value-change - Selected typography ({ property, value })
 */

interface FontFamilyOption {
  label: string;
  value: QdsTypographyFontFamily;
}

const FONT_FAMILY_OPTIONS: FontFamilyOption[] = [
  { label: 'IBM Plex Sans', value: 'ibm-plex-sans' },
  { label: 'EB Garamond', value: 'eb-garamond' },
  { label: 'Roboto', value: 'roboto' },
  { label: 'Open Sans', value: 'open-sans' }
];

const DEFAULT_PREVIEW_TEXT = 'Sample text';

loadBaseCSS();

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function cloneTypographyStyle(value: unknown): QdsTypographyStyle {
  const normalized = normalizeTypographyStyle(value);
  return normalized ? { ...normalized } : {};
}

export class QdsFontPicker extends HTMLElement implements QdsPropertyFieldPluginElement {
  private _property?: string;
  private _value: unknown = undefined;
  private _metadata?: QdsConfigField;
  private _context?: unknown;
  private _target?: Record<string, unknown>;
  private _readonly = false;
  private _open = false;
  private _draft: QdsTypographyStyle = {};
  private readonly _handleClickBound: EventListener;
  private readonly _handleChangeBound: EventListener;
  private readonly _handleInputBound: EventListener;
  private readonly _handleKeydownBound: EventListener;
  private readonly _handleValueChangeBound: EventListener;

  static get observedAttributes(): string[] {
    return ['readonly'];
  }

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this._handleClickBound = (event: Event) => this._handleClick(event as MouseEvent);
    this._handleChangeBound = (event: Event) => this._handleChange(event);
    this._handleInputBound = (event: Event) => this._handleInput(event);
    this._handleKeydownBound = (event: Event) => this._handleKeydown(event as KeyboardEvent);
    this._handleValueChangeBound = (event: Event) => this._handleValueChange(event);
    this.render();
  }

  connectedCallback(): void {
    if (!this.shadowRoot) {
      return;
    }

    this.shadowRoot.addEventListener('click', this._handleClickBound);
    this.shadowRoot.addEventListener('change', this._handleChangeBound);
    this.shadowRoot.addEventListener('input', this._handleInputBound);
    this.shadowRoot.addEventListener('keydown', this._handleKeydownBound);
    this.shadowRoot.addEventListener('value-change', this._handleValueChangeBound);
  }

  disconnectedCallback(): void {
    if (!this.shadowRoot) {
      return;
    }

    this.shadowRoot.removeEventListener('click', this._handleClickBound);
    this.shadowRoot.removeEventListener('change', this._handleChangeBound);
    this.shadowRoot.removeEventListener('input', this._handleInputBound);
    this.shadowRoot.removeEventListener('keydown', this._handleKeydownBound);
    this.shadowRoot.removeEventListener('value-change', this._handleValueChangeBound);
  }

  attributeChangedCallback(name: string, _oldValue: string | null, newValue: string | null): void {
    if (name === 'readonly') {
      this._readonly = newValue !== null;
      this.render();
    }
  }

  set property(value: string | undefined) {
    this._property = value;
  }

  get property(): string | undefined {
    return this._property;
  }

  set value(value: unknown) {
    this._value = normalizeTypographyStyle(value);
    this._draft = cloneTypographyStyle(this._value);
    this.render();
  }

  get value(): unknown {
    return this._value;
  }

  set metadata(value: QdsConfigField | undefined) {
    this._metadata = value;
    this.render();
  }

  get metadata(): QdsConfigField | undefined {
    return this._metadata;
  }

  set context(value: unknown) {
    this._context = value;
  }

  get context(): unknown {
    return this._context;
  }

  set target(value: Record<string, unknown> | undefined) {
    this._target = value;
    this.render();
  }

  get target(): Record<string, unknown> | undefined {
    return this._target;
  }

  set readonly(value: boolean) {
    this._readonly = Boolean(value);
    this.render();
  }

  get readonly(): boolean {
    return this._readonly;
  }

  /** Opens the picker dialog while preserving focus in an owning editor. */
  open(): void {
    this._openModal();
  }

  private _getDisplayValue(): QdsTypographyStyle | undefined {
    return normalizeTypographyStyle(this._value);
  }

  private _getPreviewText(): string {
    const previewProperty = typeof this._metadata?.editorOptions?.previewProperty === 'string'
      ? this._metadata.editorOptions.previewProperty
      : '';
    const previewValue = previewProperty && typeof this._target?.[previewProperty] === 'string'
      ? String(this._target?.[previewProperty] || '').trim()
      : '';
    return previewValue || this._metadata?.displayName || DEFAULT_PREVIEW_TEXT;
  }

  private _getSummary(value: unknown): string {
    return getTypographySummary(value);
  }

  private _openModal(): void {
    if (this._readonly) {
      return;
    }

    this._open = true;
    this._draft = cloneTypographyStyle(this._value);
    this.render();
  }

  private _closeModal(): void {
    this._open = false;
    this._draft = cloneTypographyStyle(this._value);
    this.render();
  }

  private _applyDraft(): void {
    const nextValue = normalizeTypographyStyle(this._draft);
    this._value = nextValue;
    this._open = false;
    this.render();
    this.dispatchEvent(new CustomEvent('value-change', {
      detail: { value: nextValue },
      bubbles: true,
      composed: true
    }));
  }

  private _resetDraft(): void {
    this._draft = {};
    this.render();
  }

  private _setDraftBoolean(
    key: 'bold' | 'italic' | 'underline' | 'strikethrough'
  ): void {
    if (this._draft[key]) {
      delete this._draft[key];
    } else {
      this._draft[key] = true;
    }
    this.render();
  }

  private _setDraftFontFamily(value: QdsTypographyFontFamily): void {
    this._draft = {
      ...this._draft,
      fontFamily: value
    };
    this.render();
  }

  private _setDraftFontSize(value: string): void {
    const trimmed = value.trim();
    if (!trimmed) {
      delete this._draft.fontSize;
      this._updatePreviewElements();
      return;
    }

    const parsed = Number.parseFloat(trimmed);
    if (!Number.isFinite(parsed)) {
      return;
    }

    this._draft = {
      ...this._draft,
      fontSize: Math.round(parsed)
    };
    this._updatePreviewElements();
  }

  private _setDraftColor(value: unknown): void {
    if (typeof value !== 'string' || !value.trim()) {
      delete this._draft.color;
      this._updatePreviewElements();
      return;
    }

    this._draft = {
      ...this._draft,
      color: value.trim()
    };
    this._updatePreviewElements();
  }

  private _updatePreviewElements(): void {
    if (!this.shadowRoot) {
      return;
    }

    const sample = this.shadowRoot.querySelector<HTMLElement>('[data-role="sample-text"]');
    const summary = this.shadowRoot.querySelector<HTMLElement>('[data-role="summary"]');
    if (sample) {
      applyTypographyStyle(sample, this._draft);
    }
    if (summary) {
      summary.textContent = this._getSummary(this._draft);
      summary.setAttribute('title', this._getSummary(this._draft));
    }
  }

  private _handleClick(event: MouseEvent): void {
    const target = event.target as HTMLElement | null;
    const actionButton = target?.closest<HTMLElement>('[data-action]');
    const action = actionButton?.dataset.action;

    if (action === 'open-modal') {
      this._openModal();
      return;
    }

    if (action === 'cancel' || action === 'close-modal') {
      this._closeModal();
      return;
    }

    if (action === 'apply') {
      this._applyDraft();
      return;
    }

    if (action === 'reset') {
      this._resetDraft();
      return;
    }

    if (action === 'clear-color') {
      this._setDraftColor(undefined);
      this.render();
      return;
    }

    if (action === 'backdrop' && target === actionButton) {
      this._closeModal();
      return;
    }

    if (this._readonly) {
      return;
    }

    const familyButton = target?.closest<HTMLElement>('[data-family]');
    const family = familyButton?.dataset.family;
    if (
      family === 'ibm-plex-sans'
      || family === 'eb-garamond'
      || family === 'roboto'
      || family === 'open-sans'
    ) {
      this._setDraftFontFamily(family);
      return;
    }

    const toggleButton = target?.closest<HTMLElement>('[data-toggle]');
    const toggle = toggleButton?.dataset.toggle;
    if (
      toggle === 'bold'
      || toggle === 'italic'
      || toggle === 'underline'
      || toggle === 'strikethrough'
    ) {
      this._setDraftBoolean(toggle);
    }
  }

  private _handleChange(event: Event): void {
    if (this._readonly) {
      return;
    }

    const target = event.target as HTMLInputElement | null;
    if (target?.dataset.role === 'font-size') {
      this._setDraftFontSize(target.value);
    }
  }

  private _handleInput(event: Event): void {
    if (this._readonly) {
      return;
    }

    const target = event.target as HTMLInputElement | null;
    if (target?.dataset.role === 'font-size') {
      this._setDraftFontSize(target.value);
    }
  }

  private _handleKeydown(event: KeyboardEvent): void {
    if (!this._open) {
      return;
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      this._closeModal();
    }
  }

  private _handleValueChange(event: Event): void {
    const target = event.target as HTMLElement | null;
    if (target?.tagName !== 'QDS-COLOR-PICKER' || target.dataset.role !== 'text-color') {
      return;
    }

    event.stopPropagation();

    const detail = (event as CustomEvent<{ value?: unknown }>).detail;
    this._setDraftColor(detail?.value);
  }

  render(): void {
    if (!this.shadowRoot) {
      return;
    }

    const currentValue = this._getDisplayValue();
    const previewText = this._getPreviewText();
    const summary = this._getSummary(this._open ? this._draft : currentValue);
    const disabledAttribute = this._readonly ? 'disabled' : '';

    this.shadowRoot.innerHTML = `
      <style>
        :host {
          display: block;
          width: auto;
          min-width: 0;
          color: var(--theme-text-primary, #333333);
          font-family: var(--theme-font-family, var(--qds-font-family, 'IBM Plex Sans', sans-serif));
        }

        *, *::before, *::after {
          box-sizing: border-box;
        }

        .font-picker {
          position: relative;
        }

        .font-picker__trigger {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 44px;
          min-width: 44px;
          min-height: 40px;
          border: 1px solid var(--theme-border-primary, #E4E9F2);
          border-radius: var(--theme-radius-sm, 4px);
          background: var(--theme-background-primary, #FFFFFF);
          color: var(--theme-text-primary, #333333);
          cursor: pointer;
          transition: border-color 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease;
        }

        .font-picker__trigger:hover:not(:disabled) {
          border-color: var(--theme-border-strong, #AFBBD1);
          background: var(--theme-background-secondary, #F8F9FA);
        }

        .font-picker__trigger:focus-visible,
        .font-picker__button:focus-visible,
        .font-picker__family:focus-visible,
        .font-picker__toggle:focus-visible,
        .font-picker__size:focus-visible,
        .font-picker__color-reset:focus-visible {
          outline: 2px solid var(--button-focus-ring, rgba(68, 94, 137, 0.2));
          outline-offset: 2px;
        }

        .font-picker__trigger:disabled {
          cursor: default;
          opacity: 0.6;
        }

        /* A formatting toolbar uses the picker as one compact control, not a
           full property-editor field. */
        :host([toolbar]) {
          display: inline-flex;
          width: 32px;
          min-width: 32px;
          vertical-align: middle;
        }

        :host([toolbar]) .font-picker__trigger {
          width: 32px;
          min-width: 32px;
          min-height: 32px;
          height: 32px;
          padding: 4px;
          border-color: transparent;
          background: transparent;
          box-shadow: none;
        }

        :host([toolbar]) .font-picker__trigger:hover:not(:disabled) {
          border-color: transparent;
          background: var(--button-hover-bg, rgba(0, 0, 0, 0.05));
        }

        .font-picker__trigger-mark {
          font-size: 13px;
          font-weight: 600;
          letter-spacing: 0.01em;
        }

        .font-picker__backdrop {
          position: fixed;
          inset: 0;
          display: none;
          align-items: center;
          justify-content: center;
          padding: var(--theme-space-5, 20px);
          background: var(--theme-background-overlay-subtle, rgba(255, 255, 255, 0.9));
          z-index: 1000;
        }

        .font-picker__backdrop[data-open="true"] {
          display: flex;
        }

        .font-picker__modal {
          display: grid;
          gap: var(--theme-space-3, 12px);
          width: min(640px, 100%);
          max-height: min(80vh, 760px);
          padding: var(--theme-space-4, 16px);
          border: 1px solid var(--theme-border-primary, #E4E9F2);
          border-radius: var(--theme-radius-md, 8px);
          background: var(--theme-background-primary, #FFFFFF);
          box-shadow: var(--theme-shadow-md, 0 8px 24px rgba(44, 51, 73, 0.1));
          overflow: auto;
        }

        .font-picker__modal-header,
        .font-picker__modal-footer {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: var(--theme-space-2, 8px);
        }

        .font-picker__title {
          margin: 0;
          font-size: var(--theme-font-size-sm, 14px);
          font-weight: var(--theme-font-weight-semibold, 600);
        }

        .font-picker__button {
          appearance: none;
          border: 1px solid var(--theme-border-primary, #E4E9F2);
          border-radius: var(--theme-radius-sm, 4px);
          background: var(--theme-background-primary, #FFFFFF);
          color: var(--theme-text-primary, #333333);
          cursor: pointer;
          font: inherit;
          min-height: 36px;
          padding: 0 12px;
        }

        .font-picker__button[data-variant="primary"] {
          border-color: var(--theme-accent-primary, #445E89);
          background: var(--theme-accent-primary, #445E89);
          color: var(--theme-text-on-accent-primary, #FFFFFF);
        }

        .font-picker__preview {
          display: grid;
          gap: var(--theme-space-2, 8px);
          padding: var(--theme-space-3, 12px);
          border: 1px solid var(--theme-border-primary, #E4E9F2);
          border-radius: var(--theme-radius-md, 8px);
          background: var(--theme-background-secondary, #F8F9FA);
        }

        .font-picker__preview-label {
          font-size: var(--theme-font-size-xs, 12px);
          color: var(--theme-text-secondary, #555555);
          text-transform: uppercase;
          letter-spacing: 0.08em;
        }

        .font-picker__preview-text {
          min-width: 0;
          color: var(--theme-text-primary, #333333);
          line-height: 1.35;
          word-break: break-word;
        }

        .font-picker__summary {
          min-width: 0;
          margin: 0;
          padding: 0 var(--theme-space-2, 8px);
          border-radius: var(--theme-radius-sm, 4px);
          background: var(--theme-background-primary, #FFFFFF);
          color: var(--theme-text-secondary, #555555);
          font-family: var(--theme-font-family-mono, ui-monospace, SFMono-Regular, Menlo, monospace);
          font-size: var(--theme-font-size-xs, 12px);
          line-height: 28px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .font-picker__group {
          display: grid;
          gap: var(--theme-space-2, 8px);
        }

        .font-picker__label {
          font-size: var(--theme-font-size-sm, 14px);
          font-weight: var(--theme-font-weight-medium, 500);
          color: var(--theme-text-primary, #333333);
        }

        .font-picker__families,
        .font-picker__toggles {
          display: flex;
          flex-wrap: wrap;
          gap: var(--theme-space-2, 8px);
        }

        .font-picker__family,
        .font-picker__toggle {
          appearance: none;
          border: 1px solid var(--theme-border-primary, #E4E9F2);
          border-radius: var(--theme-radius-sm, 4px);
          background: var(--theme-background-primary, #FFFFFF);
          color: var(--theme-text-primary, #333333);
          cursor: pointer;
          font: inherit;
          min-height: 36px;
          padding: 0 12px;
        }

        .font-picker__family[data-selected="true"],
        .font-picker__toggle[data-selected="true"] {
          border-color: var(--theme-accent-primary, #445E89);
          background: var(--theme-background-accent-subtle, rgba(68, 94, 137, 0.08));
          color: var(--theme-accent-primary, #445E89);
        }

        .font-picker__family:disabled,
        .font-picker__toggle:disabled,
        .font-picker__button:disabled,
        .font-picker__size:disabled {
          cursor: default;
          opacity: 0.6;
        }

        .font-picker__size-row {
          display: flex;
          align-items: center;
          gap: var(--theme-space-2, 8px);
        }

        .font-picker__size {
          width: 100%;
          min-height: 40px;
          padding: 0 12px;
          border: 1px solid var(--theme-border-primary, #E4E9F2);
          border-radius: var(--theme-radius-sm, 4px);
          background: var(--theme-background-primary, #FFFFFF);
          color: var(--theme-text-primary, #333333);
          font: inherit;
        }

        .font-picker__size-suffix {
          color: var(--theme-text-secondary, #555555);
          font-size: var(--theme-font-size-sm, 14px);
        }

        .font-picker__color-row {
          display: grid;
          grid-template-columns: minmax(0, 1fr) auto auto;
          align-items: center;
          gap: var(--theme-space-2, 8px);
        }

        .font-picker__color-hint {
          min-width: 0;
          color: var(--theme-text-secondary, #555555);
          font-size: var(--theme-font-size-xs, 12px);
          line-height: 1.4;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .font-picker__color-control {
          width: auto;
        }

        .font-picker__color-reset {
          appearance: none;
          border: 1px solid var(--theme-border-primary, #E4E9F2);
          border-radius: var(--theme-radius-sm, 4px);
          background: var(--theme-background-primary, #FFFFFF);
          color: var(--theme-text-secondary, #555555);
          cursor: pointer;
          font: inherit;
          min-height: 36px;
          padding: 0 10px;
        }

        .font-picker__color-reset:disabled {
          cursor: default;
          opacity: 0.6;
        }
      </style>
      <div class="font-picker">
        <button
          type="button"
          class="font-picker__trigger"
          data-action="open-modal"
          data-role="font-picker-trigger"
          aria-haspopup="dialog"
          aria-expanded="${this._open}"
          aria-label="${escapeHtml(`${this._metadata?.displayName || 'Font'}: ${summary}`)}"
          title="${escapeHtml(summary)}"
          ${disabledAttribute}
        >
          <span class="font-picker__trigger-mark">Aa</span>
        </button>
        <div class="font-picker__backdrop" data-action="backdrop" data-open="${this._open}" ${this._open ? '' : 'aria-hidden="true"'}>
          <div class="font-picker__modal" data-role="font-modal" role="dialog" aria-modal="true" aria-label="${escapeHtml(this._metadata?.displayName || 'Choose font styling')}">
            <div class="font-picker__modal-header">
              <h3 class="font-picker__title">${escapeHtml(this._metadata?.displayName || 'Font styling')}</h3>
              <button type="button" class="font-picker__button" data-action="close-modal">Close</button>
            </div>
            <div class="font-picker__preview">
              <div class="font-picker__preview-label">Preview</div>
              <div class="font-picker__preview-text" data-role="sample-text">${escapeHtml(previewText)}</div>
              <code class="font-picker__summary" data-role="summary" title="${escapeHtml(summary)}">${escapeHtml(summary)}</code>
            </div>
            <div class="font-picker__group">
              <div class="font-picker__label">Font Family</div>
              <div class="font-picker__families">
                ${FONT_FAMILY_OPTIONS.map((option) => `
                  <button
                    type="button"
                    class="font-picker__family"
                    data-family="${option.value}"
                    data-selected="${this._draft.fontFamily === option.value}"
                    ${disabledAttribute}
                  >${escapeHtml(option.label)}</button>
                `).join('')}
              </div>
            </div>
            <div class="font-picker__group">
              <div class="font-picker__label">Size</div>
              <div class="font-picker__size-row">
                <input
                  type="number"
                  class="font-picker__size"
                  data-role="font-size"
                  min="8"
                  max="160"
                  step="1"
                  placeholder="Default"
                  value="${typeof this._draft.fontSize === 'number' ? String(this._draft.fontSize) : ''}"
                  ${disabledAttribute}
                />
                <span class="font-picker__size-suffix">px</span>
              </div>
            </div>
            <div class="font-picker__group">
              <div class="font-picker__label">Style</div>
              <div class="font-picker__toggles">
                <button type="button" class="font-picker__toggle" data-toggle="bold" data-selected="${this._draft.bold === true}" ${disabledAttribute}>Bold</button>
                <button type="button" class="font-picker__toggle" data-toggle="italic" data-selected="${this._draft.italic === true}" ${disabledAttribute}>Italic</button>
                <button type="button" class="font-picker__toggle" data-toggle="underline" data-selected="${this._draft.underline === true}" ${disabledAttribute}>Underline</button>
                <button type="button" class="font-picker__toggle" data-toggle="strikethrough" data-selected="${this._draft.strikethrough === true}" ${disabledAttribute}>Strikethrough</button>
              </div>
            </div>
            <div class="font-picker__group">
              <div class="font-picker__label">Text Color</div>
              <div class="font-picker__color-row">
                <div class="font-picker__color-hint" title="${escapeHtml(this._draft.color || 'Inherited default color')}">
                  ${escapeHtml(this._draft.color || 'Inherited default color')}
                </div>
                <qds-color-picker
                  class="font-picker__color-control"
                  data-role="text-color"
                ></qds-color-picker>
                <button
                  type="button"
                  class="font-picker__color-reset"
                  data-action="clear-color"
                  ${this._readonly || !this._draft.color ? 'disabled' : ''}
                >Default</button>
              </div>
            </div>
            <div class="font-picker__modal-footer">
              <button type="button" class="font-picker__button" data-action="reset">Reset</button>
              <div>
                <button type="button" class="font-picker__button" data-action="cancel">Cancel</button>
                <button type="button" class="font-picker__button" data-action="apply" data-role="font-apply" data-variant="primary">Apply</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    `;

    const previewTextElement = this.shadowRoot.querySelector<HTMLElement>('[data-role="sample-text"]');
    if (previewTextElement) {
      applyTypographyStyle(previewTextElement, this._open ? this._draft : currentValue);
    }

    const colorPicker = this.shadowRoot.querySelector<QdsPropertyFieldPluginElement>('qds-color-picker[data-role="text-color"]');
    if (colorPicker) {
      colorPicker.value = this._draft.color || 'var(--theme-text-primary)';
      colorPicker.metadata = {
        displayName: 'Text Color',
        type: 'color'
      };
      colorPicker.readonly = this._readonly;
    }
  }
}

if (!customElements.get('qds-font-picker')) {
  customElements.define('qds-font-picker', QdsFontPicker);
}

export default QdsFontPicker;
